import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Bookmark, Calendar, Users, Tag, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RatingStars } from '@/components/community/RatingStars';
import { CommentsThread } from '@/components/community/CommentsThread';
import { useResource } from '@/hooks/useResources';
import { useResourceFavorites } from '@/hooks/useFavorites';
import { useToast } from '@/hooks/use-toast';

export default function ResourceDetail() {
  const { resourceName } = useParams<{ resourceName: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const decodedResourceName = resourceName ? decodeURIComponent(resourceName) : '';
  const { data: resource, isLoading, error } = useResource(decodedResourceName);
  const { resourceFavorites, toggleResourceFavorite } = useResourceFavorites();
  
  const isFavorite = resource ? resourceFavorites.includes(resource["Resource Name"]) : false;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="text-text-secondary">Loading resource...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-text-primary mb-4">
              Resource not found
            </h1>
            <p className="text-text-secondary mb-6">
              The resource you're looking for doesn't exist or has been moved.
            </p>
            <Button asChild>
              <Link to="/resources">Back to Resources</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleExternalLink = () => {
    if (resource["Link"]) {
      window.open(resource["Link"], '_blank', 'noopener,noreferrer');
    }
  };

  const handleToggleFavorite = () => {
    if (resource) {
      toggleResourceFavorite(resource["Resource Name"]);
      toast({
        title: isFavorite ? "Removed from favorites" : "Added to favorites",
        description: isFavorite 
          ? `${resource["Resource Name"]} removed from your favorites`
          : `${resource["Resource Name"]} added to your favorites`,
      });
    }
  };

  // Parse topic tags and use cases
  const topics = resource["Topic Tags"] 
    ? resource["Topic Tags"].split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const useCases = resource["Recommended Use Case"]
    ? resource["Recommended Use Case"].split(/[,•\n]/).map(c => c.trim()).filter(Boolean)
    : [];

  // Create deterministic UUID from resource name for community features
  const createResourceId = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to UUID format using the hash
    const hex = (Math.abs(hash).toString(16) + '000000000').substring(0, 8);
    return `${hex.substring(0, 8)}-0000-4000-8000-${hex}${hex.substring(0, 4)}`;
  };
  const resourceId = createResourceId(resource["Resource Name"]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Resources
        </Button>

        {/* Hero section */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="secondary" className="px-3 py-1">
                    {resource["Content Type"]}
                  </Badge>
                  {resource["Last Updated"] && (
                    <div className="flex items-center gap-1 text-sm text-text-tertiary">
                      <Calendar className="h-4 w-4" />
                      Updated {resource["Last Updated"]}
                    </div>
                  )}
                </div>
                
                <CardTitle className="text-3xl font-heading font-bold text-text-primary mb-4">
                  {resource["Resource Name"]}
                </CardTitle>
                
                {resource["Description"] && (
                  <p className="text-lg text-text-secondary leading-relaxed">
                    {resource["Description"]}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
                {resource["Link"] && (
                  <Button onClick={handleExternalLink} size="lg" className="w-full">
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Open Resource
                  </Button>
                )}
                
                <Button 
                  onClick={handleToggleFavorite}
                  variant={isFavorite ? "default" : "outline"}
                  size="lg" 
                  className="w-full"
                >
                  <Bookmark className={`h-5 w-5 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                  {isFavorite ? 'Bookmarked' : 'Bookmark'}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content area */}
          <div className="lg:col-span-2 space-y-4">
            {/* Topics and Use Cases in same row on larger screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Topics */}
              {topics.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-heading font-semibold">
                      Topics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {topics.map((topic, index) => (
                        <Badge 
                          key={index} 
                          variant="outline"
                          className="px-3 py-1"
                        >
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Use Cases */}
              {useCases.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-heading font-semibold">
                      Recommended Use Cases
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {useCases.map((useCase, index) => (
                        <Badge 
                          key={index} 
                          variant="accent"
                          className="px-3 py-1"
                        >
                          {useCase}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Comments Section - Hidden on tablet/mobile, shown on desktop */}
            <Card className="hidden lg:block">
              <CardContent className="pt-6">
                <CommentsThread entityType="resource" entityId={resourceId} />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Resource Details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading font-semibold">
                  Resource Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-text-secondary">Content Type</dt>
                  <dd className="text-sm text-text-primary mt-1">{resource["Content Type"]}</dd>
                </div>
                
                {resource["Last Updated"] && (
                  <div>
                    <dt className="text-sm font-medium text-text-secondary">Last Updated</dt>
                    <dd className="text-sm text-text-primary mt-1">
                      {resource["Last Updated"]}
                    </dd>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading font-semibold">
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {resource["Link"] && (
                  <Button 
                    onClick={handleExternalLink} 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Resource
                  </Button>
                )}
                
                <Button 
                  onClick={handleToggleFavorite}
                  variant="outline" 
                  className="w-full justify-start"
                >
                  <Bookmark className={`h-4 w-4 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  asChild
                >
                  <Link to="/resources">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Resources
                  </Link>
                </Button>
              </CardContent>
            </Card>
            
            {/* Community Rating */}
            <Card>
              <CardContent className="pt-6">
                <RatingStars entityType="resource" entityId={resourceId} />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Comments Section - Shown on tablet/mobile at bottom */}
        <Card className="lg:hidden mt-6">
          <CardContent className="pt-6">
            <CommentsThread entityType="resource" entityId={resourceId} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}